import { useMutation } from "@tanstack/react-query";
import { Link, useSearch } from "@tanstack/react-router";
import { type FormEvent, type ReactNode, useEffect, useRef, useState } from "react";
import { TabNav } from "../../components/TabNav";
import { useToast } from "../../components/ui";
import { ApiError, api } from "../../lib/api";
import { useCompany, useLumiConfig, useMe, useUpdateLumiConfig } from "../../lib/queries";
import type { LumiConfig, LumiTestResult } from "../../lib/types";
import { AiCard } from "./AiCard";
import { FirstSteps } from "./FirstSteps";

export const LUMI_TABS = ["habla", "ofrece", "datos", "precios", "postventa"] as const;
export type LumiTab = (typeof LUMI_TABS)[number];

const TAB_LABEL: Record<LumiTab, string> = {
  habla: "Cómo habla",
  ofrece: "Qué ofrece",
  datos: "Datos y traspaso",
  precios: "Precios",
  postventa: "Postventa",
};

/** Lumi, the IA that answers WhatsApp: the switch, its messages, a test chat and its settings. */
export function LumiPage() {
  const { tab = "habla" } = useSearch({ from: "/app/lumi" });
  const config = useLumiConfig();
  const update = useUpdateLumiConfig();
  const toast = useToast();
  const [draft, setDraft] = useState<Partial<LumiConfig>>({});
  const saved = config.data;
  const value = saved ? { ...saved, ...draft } : null;
  const dirty = !!saved && Object.entries(draft).some(([k, v]) => !same(v, saved[k as keyof LumiConfig]));
  const set = <K extends keyof LumiConfig>(k: K, v: LumiConfig[K]) => setDraft((d) => ({ ...d, [k]: v }));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    try {
      await update.mutateAsync(draft);
      setDraft({});
      toast("Lumi guardada: las conversaciones nuevas ya usan estos cambios");
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "No se pudo guardar.");
    }
  }

  return (
    <div className="page">
      <div className="page-head">
        <h1>Lumi</h1>
      </div>
      <FirstSteps />
      <AiCard />
      <TestChat />
      <TabNav label="Lumi">
        {LUMI_TABS.map((t) => (
          <Link
            key={t}
            to="/lumi"
            search={t === "habla" ? {} : { tab: t }}
            activeOptions={{ exact: true, includeSearch: true }}
            aria-current={tab === t ? "page" : undefined}
          >
            {TAB_LABEL[t]}
          </Link>
        ))}
      </TabNav>
      {!value ? (
        <p className="muted">{config.isError ? "No se pudo cargar la configuración." : "Cargando…"}</p>
      ) : (
        <form className="lumi-form" onSubmit={onSubmit}>
          {tab === "habla" && <TalkTab value={value} set={set} />}
          {tab === "ofrece" && <OfferTab value={value} set={set} />}
          {tab === "datos" && <DataTab value={value} set={set} />}
          {tab === "precios" && <PricesTab value={value} set={set} />}
          {tab === "postventa" && <PostSaleTab value={value} set={set} />}
          {dirty && (
            <div className="product-save">
              <button className="btn" type="button" onClick={() => setDraft({})}>
                Descartar
              </button>
              <button className="btn btn-primary" type="submit" disabled={update.isPending}>
                {update.isPending ? "Guardando…" : "Guardar cambios"}
              </button>
            </div>
          )}
        </form>
      )}
    </div>
  );
}

const same = (a: unknown, b: unknown) =>
  JSON.stringify(a ?? null) === JSON.stringify(b ?? null) || ((a ?? "") === "" && (b ?? "") === "");

interface TabProps {
  value: LumiConfig;
  set: <K extends keyof LumiConfig>(k: K, v: LumiConfig[K]) => void;
}

/** A set of options shown as cards; one is chosen. */
export function Choice<T extends string>({
  name,
  label,
  value,
  options,
  onChange,
}: {
  name: string;
  label: string;
  value: T;
  options: { value: T; label: string; hint?: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <fieldset className="choice">
      <legend>{label}</legend>
      <div className="choice-options">
        {options.map((o) => (
          <label key={o.value} className={value === o.value ? "chosen" : undefined}>
            <input
              type="radio"
              name={name}
              value={o.value}
              checked={value === o.value}
              onChange={() => onChange(o.value)}
            />
            <span className="choice-label">{o.label}</span>
            {o.hint && <span className="choice-hint">{o.hint}</span>}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export function TextArea({
  id,
  label,
  hint,
  value,
  max,
  rows = 4,
  placeholder,
  onChange,
}: {
  id: string;
  label: string;
  hint?: ReactNode;
  value: string | null;
  max: number;
  rows?: number;
  placeholder?: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <textarea
        className="textarea"
        id={id}
        rows={rows}
        maxLength={max}
        placeholder={placeholder}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
      />
      {hint && <span className="faint">{hint}</span>}
    </div>
  );
}

function TalkTab({ value, set }: TabProps) {
  const company = useCompanyName();
  return (
    <div className="card">
      <div className="card-body">
        <div className="field">
          <label htmlFor="lumi-name">Nombre del asistente (opcional)</label>
          <input
            className="input"
            id="lumi-name"
            maxLength={40}
            placeholder="Por ejemplo: Sofía"
            value={value.assistantName ?? ""}
            onChange={(e) => set("assistantName", e.target.value)}
          />
          <span className="faint">
            Así se presenta con tus clientes. Vacío: dice que es el asistente virtual de {company}. Si le preguntan si
            es una persona, siempre dice la verdad.
          </span>
        </div>
        <Choice
          name="address"
          label="Cómo trata al cliente"
          value={value.address}
          onChange={(v) => set("address", v)}
          options={[
            { value: "usted", label: "Usted", hint: "¿En qué le puedo ayudar?" },
            { value: "tu", label: "Tú", hint: "¿En qué te puedo ayudar?" },
            { value: "vos", label: "Vos", hint: "¿En qué te puedo ayudar? ¿Tenés…?" },
          ]}
        />
        <Choice
          name="tone"
          label="Tono"
          value={value.tone}
          onChange={(v) => set("tone", v)}
          options={[
            { value: "cercano", label: "Cercano", hint: "Amable y natural" },
            { value: "formal", label: "Formal", hint: "Respetuoso y sobrio" },
          ]}
        />
        <Choice
          name="emoji"
          label="Emojis"
          value={value.emojiUse}
          onChange={(v) => set("emojiUse", v)}
          options={[
            { value: "never", label: "Nunca" },
            { value: "mirror", label: "Si el cliente los usa" },
            { value: "some", label: "De vez en cuando" },
          ]}
        />
        <Choice
          name="length"
          label="Largo de las respuestas"
          value={value.replyLength}
          onChange={(v) => set("replyLength", v)}
          options={[
            { value: "short", label: "Cortas", hint: "1 a 4 frases" },
            { value: "normal", label: "Medias", hint: "Hasta 6 frases" },
            { value: "detailed", label: "Detalladas", hint: "Lo que haga falta" },
          ]}
        />
        <TextArea
          id="lumi-instructions"
          label="Indicaciones para Lumi"
          max={2000}
          rows={5}
          value={value.instructions}
          onChange={(v) => set("instructions", v)}
          placeholder="Por ejemplo: Si preguntan por envíos fuera del GAM, explica que van por encomienda y tardan 2 o 3 días."
          hint="En tus palabras, lo que Lumi debe tener en cuenta. Las reglas de seguridad de Orbis siempre se respetan."
        />
        <Choice
          name="analysis"
          label="Análisis de cada mensaje"
          value={value.analysisMode}
          onChange={(v) => set("analysisMode", v)}
          options={[
            { value: "auto", label: "Automático", hint: "Recomendado: piensa más solo cuando la pregunta lo pide" },
            { value: "fast", label: "Rápida", hint: "Responde más rápido" },
            { value: "balanced", label: "Equilibrada", hint: "Piensa un poco más en todo" },
            { value: "detailed", label: "Detallada", hint: "Piensa más en todo; tarda más" },
          ]}
        />
      </div>
    </div>
  );
}

function OfferTab({ value, set }: TabProps) {
  const custom = value.offerType !== "catalog";
  return (
    <>
      <div className="card">
        <div className="card-body">
          <Choice
            name="offer"
            label="¿Qué vende la empresa?"
            value={value.offerType}
            onChange={(v) => set("offerType", v)}
            options={[
              { value: "catalog", label: "Un catálogo", hint: "Productos o servicios definidos" },
              { value: "custom", label: "A la medida", hint: "Cada trabajo se cotiza aparte" },
              { value: "mixed", label: "Las dos cosas", hint: "Catálogo y trabajos a la medida" },
            ]}
          />
          {custom && (
            <>
              <p className="faint">
                Lumi pregunta lo necesario, guarda el requerimiento en la ficha del cliente y lo pasa a un asesor. Nunca
                confirma si se puede hacer, el precio ni el plazo.
              </p>
              <TextArea
                id="lumi-we-do"
                label="Lo que hacemos"
                max={2000}
                value={value.customWeDo}
                onChange={(v) => set("customWeDo", v)}
                placeholder="Ej.: Muebles de cocina y closets en melamina o madera, a la medida, con instalación en el GAM."
              />
              <TextArea
                id="lumi-we-dont"
                label="Lo que no hacemos"
                max={2000}
                value={value.customWeDont}
                onChange={(v) => set("customWeDont", v)}
                placeholder="Ej.: Reparaciones de muebles que no fabricamos, ni trabajos fuera del GAM."
              />
              <TextArea
                id="lumi-need"
                label="Lo que necesitamos saber para cotizar"
                max={2000}
                value={value.customNeedToKnow}
                onChange={(v) => set("customNeedToKnow", v)}
                placeholder={
                  "Uno por línea. Ej.:\nMedidas del espacio\nMaterial y color\nFecha deseada\nProvincia y cantón"
                }
              />
            </>
          )}
          <TextArea
            id="lumi-topics"
            label="Temas en los que ayuda"
            max={2000}
            rows={4}
            value={value.topics}
            onChange={(v) => set("topics", v)}
            placeholder={"Uno por línea. Ej.:\nProductos y sus características\nGarantías y manuales\nDónde comprar"}
            hint="Lo que no esté aquí ni en el catálogo o el conocimiento, Lumi lo rechaza con amabilidad."
          />
          <p className="faint">
            Qué hace la empresa, su contacto y su horario se editan en <Link to="/empresa">Empresa</Link>.
          </p>
        </div>
      </div>
      <div className="card">
        <div className="card-body">
          <Choice
            name="recommend"
            label="¿Qué puede recomendar Lumi?"
            value={value.recommendScope}
            onChange={(v) => set("recommendScope", v)}
            options={[
              { value: "catalog_only", label: "Solo mi catálogo", hint: "Recomendado" },
              {
                value: "complements",
                label: "También complementos",
                hint: "Cosas que no vendo, en genérico, nunca de mis categorías",
              },
              { value: "open", label: "Asesoría abierta", hint: "Consejos generales sobre el tema" },
            ]}
          />
          <p className="faint">Nunca nombra marcas, tiendas ni precios de otros.</p>
          <TextArea
            id="lumi-never"
            label="Nunca recomiendes (opcional)"
            max={1000}
            rows={2}
            value={value.neverRecommend}
            onChange={(v) => set("neverRecommend", v)}
            placeholder="Ej.: Instalar el gas sin un técnico autorizado"
          />
          <TextArea
            id="lumi-may"
            label="Puedes sugerir (opcional)"
            max={1000}
            rows={2}
            value={value.maySuggest}
            onChange={(v) => set("maySuggest", v)}
            placeholder="Ej.: Un regulador de gas nuevo con cada plantilla"
          />
        </div>
      </div>
    </>
  );
}

/** What Lumi says when these are left empty (same as core's DEFAULT_MESSAGES). */
const DEFAULT_TEXT = {
  usted: {
    handoff: "Le paso con un asesor del equipo, en un momento le atiende.",
    refusal:
      "Solo puedo ayudarle con consultas sobre nuestros productos, garantías y servicios. ¿En qué le puedo ayudar?",
  },
  tu: {
    handoff: "Te paso con un asesor del equipo, en un momento te atiende.",
    refusal:
      "Solo puedo ayudarte con consultas sobre nuestros productos, garantías y servicios. ¿En qué te puedo ayudar?",
  },
  vos: {
    handoff: "Te paso con alguien del equipo, en un momento te atiende.",
    refusal: "Solo te puedo ayudar con consultas sobre nuestros productos, garantías y servicios. ¿En qué te ayudo?",
  },
} as const;

const FIELD_OPTIONS = [
  { value: "name", label: "Nombre" },
  { value: "location", label: "Provincia y cantón" },
  { value: "customer_type", label: "Si compra para la casa, un negocio o una institución" },
  { value: "email", label: "Correo" },
] as const;

function DataTab({ value, set }: TabProps) {
  const company = useCompanyName();
  const toggle = (f: LumiConfig["collectFields"][number]) =>
    set(
      "collectFields",
      value.collectFields.includes(f) ? value.collectFields.filter((x) => x !== f) : [...value.collectFields, f],
    );
  return (
    <>
      <div className="card">
        <div className="card-body">
          <h3>Datos del cliente</h3>
          <div className="field">
            <label htmlFor="lumi-greeting">Saludo de la primera respuesta</label>
            <input
              className="input"
              id="lumi-greeting"
              maxLength={300}
              placeholder={`Por ejemplo: ¡Hola! Gracias por escribir a ${company}.`}
              value={value.greeting ?? ""}
              onChange={(e) => set("greeting", e.target.value)}
            />
          </div>
          <fieldset className="choice">
            <legend>Datos que pide y guarda en el contacto</legend>
            <div className="lumi-checks">
              {FIELD_OPTIONS.map((f) => (
                <label key={f.value} className="check">
                  <input
                    type="checkbox"
                    checked={value.collectFields.includes(f.value)}
                    onChange={() => toggle(f.value)}
                  />
                  {f.label}
                </label>
              ))}
            </div>
          </fieldset>
          <Choice
            name="collect"
            label="Cuándo los pide"
            value={value.collectMode}
            onChange={(v) => set("collectMode", v)}
            options={[
              { value: "off", label: "No los pide" },
              { value: "first", label: "En la primera respuesta", hint: "Responde la pregunta y pide los datos" },
              { value: "natural", label: "Cuando venga al caso", hint: "Por ejemplo, la provincia al buscar tienda" },
              { value: "strict", label: "Antes de dar información", hint: "Primero los datos, después responde" },
            ]}
          />
        </div>
      </div>
      <div className="card">
        <div className="card-body">
          <h3>Paso a un asesor</h3>
          <p className="muted">
            Siempre pasa a una persona cuando el cliente la pide, quiere comprar o cotizar, tiene un reclamo o hay un
            riesgo de seguridad, y cuando no encuentra la respuesta.
          </p>
          <TextArea
            id="lumi-handoff-rules"
            label="Además, que pase a un asesor cuando…"
            max={2000}
            value={value.handoffRules}
            onChange={(v) => set("handoffRules", v)}
            placeholder="Por ejemplo: el cliente es un negocio que pide más de 10 unidades."
          />
          <TextArea
            id="lumi-handoff"
            label="Mensaje al pasar a un asesor"
            max={500}
            rows={2}
            value={value.handoffMessage}
            onChange={(v) => set("handoffMessage", v)}
            placeholder={DEFAULT_TEXT[value.address].handoff}
          />
          <TextArea
            id="lumi-refusal"
            label="Mensaje para preguntas fuera de tema"
            max={500}
            rows={2}
            value={value.refusalMessage}
            onChange={(v) => set("refusalMessage", v)}
            placeholder={DEFAULT_TEXT[value.address].refusal}
          />
        </div>
      </div>
      <OffHoursCard value={value} set={set} />
    </>
  );
}

/**
 * Outside the company's hours: Lumi answers as usual, or one fixed message and the team
 * takes the conversation on opening (no Lumi messages spent).
 */
function OffHoursCard({ value, set }: TabProps) {
  const schedule = useCompany().data?.schedule;
  const toYou = value.address === "usted" ? "le" : "te";
  return (
    <div className="card">
      <div className="card-body">
        <div>
          <h3>Fuera de horario</h3>
          <p className="muted">
            Lo que pasa cuando un cliente escribe con la empresa cerrada, según el horario de Empresa → Horario.
          </p>
        </div>
        <Choice
          name="off-hours"
          label="Cuando la empresa está cerrada"
          value={value.offHoursMode}
          onChange={(v) => set("offHoursMode", v)}
          options={[
            {
              value: "lumi",
              label: "Lumi atiende",
              hint: "Responde como siempre; si pasa a un asesor, avisa cuándo abren",
            },
            {
              value: "message",
              label: "Solo un mensaje fijo",
              hint: "Se envía una vez por conversación y no gasta mensajes de Lumi; el equipo la atiende al abrir",
            },
          ]}
        />
        {value.offHoursMode === "message" && (
          <TextArea
            id="lumi-off-hours"
            label="Mensaje fuera de horario"
            hint={
              <>
                Escribe <span className="mono">{"{apertura}"}</span> donde quieras que diga cuándo abren (por ejemplo,
                "mañana a las 8 a. m.").
              </>
            }
            max={500}
            rows={2}
            value={value.offHoursMessage}
            onChange={(v) => set("offHoursMessage", v)}
            placeholder={`¡Gracias por escribir! En este momento estamos cerrados: ${toYou} respondemos {apertura}.`}
          />
        )}
        {!schedule && (
          <p className="faint">
            Todavía no hay horario en Empresa → Horario: mientras tanto, Orbis la toma como abierta.
          </p>
        )}
      </div>
    </div>
  );
}

function PricesTab({ value, set }: TabProps) {
  return (
    <div className="card">
      <div className="card-body">
        <Choice
          name="prices"
          label="¿Lumi da precios?"
          value={value.pricePolicy}
          onChange={(v) => set("pricePolicy", v)}
          options={[
            { value: "no_share", label: "No", hint: "Cuando preguntan un precio, pasa a un asesor" },
            { value: "public", label: "Sí, los del catálogo", hint: "Solo los precios cargados en cada producto" },
            {
              value: "by_customer_type",
              label: "Según el tipo de cliente",
              hint: "Precio para la casa, para negocios o instituciones",
            },
          ]}
        />
        {value.pricePolicy !== "no_share" && (
          <p className="faint">
            Lumi nunca inventa un precio: si un producto no tiene precio en el catálogo, pasa a un asesor.
          </p>
        )}
      </div>
    </div>
  );
}

/** Customers who already bought (f4-01): what Lumi handles and what goes to a person. */
function PostSaleTab({ value, set }: TabProps) {
  return (
    <div className="card">
      <div className="card-body">
        <div>
          <h3>Clientes que ya compraron</h3>
          <p className="muted">
            Fallas, cambios, devoluciones y facturas siempre pasan a un asesor. Antes, si faltan, Lumi pide los datos
            del cliente. Lumi nunca confirma que una garantía aplica: eso lo decide el equipo al revisar.
          </p>
        </div>
        <Choice
          name="post-sale-help"
          label="Dudas de uso y de garantía"
          value={value.postSaleHelp}
          onChange={(v) => set("postSaleHelp", v)}
          options={[
            {
              value: "answer",
              label: "Lumi responde",
              hint: "Con el manual y la política; si no se resuelve, pasa a un asesor",
            },
            { value: "handoff", label: "Pasar a un asesor", hint: "Lumi no intenta resolverlas" },
          ]}
        />
        <div className="ai-card post-sale-lookup">
          <div>
            <strong>Lumi dice en qué estado va un caso</strong>
            <p className="muted">
              Solo los casos del mismo número de WhatsApp. Después pregunta si el cliente quiere hablar con un asesor.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={value.caseLookup}
            aria-label="Lumi dice en qué estado va un caso"
            className="switch"
            onClick={() => set("caseLookup", !value.caseLookup)}
          >
            <span className="switch-thumb" />
          </button>
        </div>
        <p className="faint">Repuestos y accesorios: Lumi los busca en el catálogo y, si no están, pasa a un asesor.</p>
        <TextArea
          id="lumi-post-sale"
          label="Indicaciones de postventa (opcional)"
          hint="Lo propio de la empresa. Las reglas de Orbis y las de seguridad siempre mandan."
          value={value.postSaleInstructions}
          max={2000}
          rows={3}
          placeholder="Ej.: Los cambios solo dentro de 8 días con la caja original; pide una foto del daño."
          onChange={(v) => set("postSaleInstructions", v)}
        />
      </div>
    </div>
  );
}

function useCompanyName() {
  const me = useMe().data;
  const active = me?.tenants.find((t) => t.tenantId === me.activeTenantId) ?? me?.tenants[0];
  return active?.tenantName ?? "la empresa";
}

interface Turn {
  role: "user" | "assistant";
  content: string;
  handoff?: boolean;
}

/**
 * "Probar la IA": the owner plays the customer with the saved settings (save first to
 * try a change). Nothing reaches WhatsApp; each reply spends a message, 20 a day.
 */
function TestChat() {
  const [open, setOpen] = useState(false);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [contact, setContact] = useState<Record<string, string>>({});
  const [text, setText] = useState("");
  const [left, setLeft] = useState<number | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const log = useRef<HTMLDivElement>(null);
  const send = useMutation({
    mutationFn: (history: Turn[]) =>
      api.post<LumiTestResult>("/ai/test", {
        history: history.map(({ role, content }) => ({ role, content })),
        contact: CONTACT_KEYS.some((k) => contact[k]) ? toKnown(contact) : undefined,
      }),
  });

  useEffect(() => {
    log.current?.scrollTo({ top: log.current.scrollHeight, behavior: "smooth" });
  });

  async function onSend(e: FormEvent) {
    e.preventDefault();
    const message = text.trim();
    if (!message || send.isPending) return;
    const history: Turn[] = [...turns, { role: "user", content: message }];
    setTurns(history);
    setText("");
    setNotice(null);
    try {
      const r = await send.mutateAsync(history);
      setLeft(r.testsLeft);
      if (r.status === "ok") {
        setTurns([...history, { role: "assistant", content: r.reply, handoff: r.handoff }]);
        setContact((c) => ({ ...c, ...r.contactUpdates }));
      } else {
        setTurns(turns);
        setText(message);
        setNotice(
          r.status === "daily_limit"
            ? "Ya usaste las 20 pruebas de hoy. Mañana hay 20 más."
            : "No quedan mensajes de Lumi. Pide una recarga para seguir probando.",
        );
      }
    } catch {
      setTurns(turns);
      setText(message);
      setNotice("Lumi no respondió. Intenta de nuevo.");
    }
  }

  if (!open) {
    return (
      <div className="card">
        <div className="card-body">
          <div className="account-head">
            <div>
              <h3>Probar la IA</h3>
              <p className="muted">
                Escríbele como si fueras un cliente, con la configuración guardada. No se envía nada por WhatsApp; cada
                respuesta usa un mensaje de Lumi (20 pruebas al día).
              </p>
            </div>
            <button className="btn btn-primary" type="button" onClick={() => setOpen(true)}>
              Probar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card lumi-test">
      <div className="card-body">
        <div className="account-head">
          <div>
            <h3>Probar la IA</h3>
            <p className="muted">
              {left === null ? "20 pruebas al día." : `Quedan ${left} pruebas hoy.`} Guarda los cambios de abajo para
              probarlos.
            </p>
          </div>
          <div className="lumi-test-actions">
            <button
              className="btn"
              type="button"
              disabled={!turns.length || send.isPending}
              onClick={() => {
                setTurns([]);
                setContact({});
                setNotice(null);
              }}
            >
              Empezar de nuevo
            </button>
            <button className="btn" type="button" onClick={() => setOpen(false)}>
              Cerrar
            </button>
          </div>
        </div>
        <div className="lumi-test-log" ref={log} aria-live="polite">
          {!turns.length && <p className="faint">Por ejemplo: “Hola, ¿tienen plantillas de 4 quemadores?”</p>}
          {turns.map((t, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: turns only grow or reset
            <div key={i} className={`bubble ${t.role === "user" ? "out" : "in"}`}>
              {t.content}
              {t.handoff && <span className="lumi-test-handoff">Pasaría la conversación a un asesor</span>}
            </div>
          ))}
          {send.isPending && <div className="bubble in typing">Lumi está escribiendo…</div>}
        </div>
        {Object.keys(contact).length > 0 && (
          <p className="faint">
            Guardaría en la ficha:{" "}
            {Object.entries(contact)
              .map(([k, v]) => `${CONTACT_LABEL[k] ?? k}: ${TYPE_LABEL[v] ?? v}`)
              .join(" · ")}
          </p>
        )}
        {notice && (
          <p className="error-text" role="alert">
            {notice}
          </p>
        )}
        <form className="lumi-test-input" onSubmit={onSend}>
          <input
            className="input"
            aria-label="Mensaje del cliente"
            placeholder="Escribe como cliente…"
            maxLength={2000}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <button className="btn btn-primary" type="submit" disabled={!text.trim() || send.isPending}>
            Enviar
          </button>
        </form>
      </div>
    </div>
  );
}

const CONTACT_KEYS = ["name", "province", "canton", "type", "email"];
const CONTACT_LABEL: Record<string, string> = {
  name: "Nombre",
  province: "Provincia",
  canton: "Cantón",
  type: "Tipo",
  email: "Correo",
};
const TYPE_LABEL: Record<string, string> = { consumer: "Casa", business: "Negocio", institution: "Institución" };
const toKnown = (c: Record<string, string>) =>
  Object.fromEntries(CONTACT_KEYS.filter((k) => c[k]).map((k) => [k, c[k]]));
